import { Event } from '@yandex-int/messenger.utils';
import {
    ActiveDevices,
    AudioInputDevice,
    Device,
    DeviceOrQuery,
    DevicesChangedEvent,
    QueryDevices,
    RequestedDevices,
    Session,
    SessionConfig,
    SessionEvent,
    VideoInputDevice,
    VideoProcessorOptions,
} from '@yandex-video-platform/goloom-sdk';
import isNil from 'lodash/isNil';
import { call, select } from 'typed-redux-saga';

import { browser } from '../../helpers/browser-capabilities';
import { bind } from '../../helpers/decorators';
import { COUNTERS } from '../../helpers/logger-enums';
import { createLogger } from '../../helpers/loggerFacade';
import { QueueWithIgnores } from '../../helpers/queue-with-ignores';
import { getDesktopVideoConstraints, getMobileVideoConstraints } from '../../helpers/video-resolution-constraints';
import {
    DEFAULT_FLAGS,
    getNoiseCancellationValueByType,
    getSoundProcessingOptions,
    isInputDevice,
} from '../goloom-sdk';
import { GoloomSDK } from '../goloom-sdk/goloom-sdk';
import { selectInitSessionConfig } from '../goloom-sdk/selectors';
import { FacingMode, NoiseCancellationType, selectNoiseCancellationType } from '../settings';
import { waitForSettingsLoaded } from '../settings/sagas';
import { getDeviceErrorCode } from './helpers/get-device-error-code';
import { PreviewDevicesTasks, sessionPreviewQueueIgnoreMap } from './preview-devices-constants';
import { ResetDeviceErrorPayload, SetDeviceErrorPayload } from './slice';

const logger = createLogger('device-manager');

export class DeviceManager {
    private goloomSdk: GoloomSDK;

    public sessionPreview: Maybe<Session>;
    public onAudioStatusChanged = new Event<boolean>();
    public onVideoStatusChanged = new Event<boolean>();
    public onActiveDevicesChanged = new Event<ActiveDevices>();
    public onDeviceChanged = new Event<DevicesChangedEvent>();
    public onError = new Event<SetDeviceErrorPayload>();
    public onResetError = new Event<ResetDeviceErrorPayload>();
    public onInit = new Event<boolean>();

    private sessionPreviewTaskQueue: QueueWithIgnores;
    private initialConfig: Maybe<SessionConfig>;

    private tempNoiseCancellationType: Maybe<NoiseCancellationType>;
    private currentNoiseCancellationType: Maybe<NoiseCancellationType>;
    constructor(sdk: GoloomSDK) {
        this.goloomSdk = sdk;
        this.sessionPreviewTaskQueue = new QueueWithIgnores(sessionPreviewQueueIgnoreMap);

        this.goloomSdk.onInit.addListener(this.subscribeToEvents);
        this.goloomSdk.onJoin.addListener(this.syncMuteStateOnJoin);
        this.goloomSdk.onDestroy.addListener(this.unsubscribeFromEvents);
    }

    public *init() {
        if (this.sessionPreview) {
            return;
        }

        // Ждём загрузки настроек из localStorage перед созданием сессии
        yield* call(waitForSettingsLoaded, 'DeviceManager.init', logger);

        const config = yield* select(selectInitSessionConfig);
        const noiseCancellationType = yield* select(selectNoiseCancellationType);
        const sessionConfig = (this.initialConfig = {
            soundProcessing: config.soundProcessing,
            videoProcessing: config.videoProcessing,
            flags: {
                ...DEFAULT_FLAGS,
                keepDefaultDevices: true,
                ...config.flags,
            },
        });
        this.currentNoiseCancellationType = noiseCancellationType;
        this.tempNoiseCancellationType = noiseCancellationType;
        this.initialConfig = sessionConfig;
        this.createSessionPreview();
        this.onInit.dispatch(true);
    }

    public createSessionPreview() {
        const initialConfig = this.initialConfig;

        this.sessionPreview = new Session(initialConfig);
        this.sessionPreview.addAsyncEventListener('activeDevicesChanged', this.handlePreviewActiveDevicesChanged);
        this.sessionPreview.addAsyncEventListener('deviceChanged', this.handlePreviewDeviceChanged);
    }

    public reinitSessionPreview = async function (this: DeviceManager) {
        return this.sessionPreviewTaskQueue.runTask(async () => {
            await this.destroySessionPreview();
            this.createSessionPreview();
        }, PreviewDevicesTasks.ReinitSessionPreview);
    };

    public async destroySessionPreview() {
        if (!this.sessionPreview) {
            return;
        }

        this.sessionPreview.removeAsyncEventListener('activeDevicesChanged', this.handlePreviewActiveDevicesChanged);
        this.sessionPreview.removeAsyncEventListener('deviceChanged', this.handlePreviewDeviceChanged);
        const prevSessionPreview = this.sessionPreview;
        await this.sessionPreview.destroy();

        // Если во время уничтожения создали новую сессию, то не затираем новую сессию
        if (prevSessionPreview === this.sessionPreview) {
            this.sessionPreview = undefined;
        }
    }

    @bind
    public async requestDevices(query?: QueryDevices): Promise<RequestedDevices> {
        if (!this.sessionPreview) {
            return Promise.resolve({ audioInput: [], videoInput: [], audioOutput: [] });
        }

        return await this.sessionPreview.requestDevices(query);
    }

    public *setDevices(devices: DeviceOrQuery[]) {
        if (!this.goloomSdk.session || !this.sessionPreview) {
            logger.event('Session is not initialized on setDevices', {
                additional: {
                    method: 'setDevices',
                    devices,
                },
            });
            return;
        }

        const selectedDevicesBeforeApply = this.goloomSdk.session.selectedDevices;
        const activeDevicesBeforeApply = this.goloomSdk.session.activeDevices;

        try {
            for (let i = 0; i < devices.length; i++) {
                const device = devices[i];
                yield* call([this.goloomSdk.session, this.goloomSdk.session.selectDevice], device);
            }
            yield* call([this.goloomSdk.session, this.goloomSdk.session.applySelectedDevices]);

            logger.event('Devices have been successfully applied', {
                additional: {
                    method: 'setDevices',
                    devices,
                },
            });

            // Если ошибок не получили и дошли до сюда, то очищаем ошибки из стора
            devices.forEach(({ kind }) => {
                if (kind === 'videoinput' && this.goloomSdk.session?.videoStatus) {
                    this.onResetError.dispatch({ kind });
                }

                if (kind === 'audioinput' && this.goloomSdk.session?.audioStatus) {
                    this.onResetError.dispatch({ kind });
                }

                if (kind === 'audiooutput') {
                    this.onResetError.dispatch({ kind });
                }
            });
        } catch (error) {
            devices.forEach(({ kind }) => {
                this.onError.dispatch({
                    kind,
                    error: getDeviceErrorCode(error),
                });
            });

            logger.warn(error, {
                method: 'setDevices',
                additional: {
                    desiredDevices: devices,
                    selectedDevicesBeforeCall: selectedDevicesBeforeApply,
                    activeDevicesBeforeCall: activeDevicesBeforeApply,
                },
            });

            throw error;
        }
    }

    public async setAudioStatus(status: boolean, isOptimistic = false) {
        if (!this.goloomSdk.session) {
            logger.event('Session is not initialized on setAudioStatus', {
                additional: {
                    method: 'setAudioStatus',
                    type: 'audio',
                    status,
                },
            });
            return;
        }

        try {
            if (status === true && !this.goloomSdk.session.selectedDevices.audioInput && !isOptimistic) {
                throw new Error('No audio input device selected');
            }
            if (status === true) {
                logger.startDelta(COUNTERS.UNMUTE_MICROPHONE_DELTA);
            }

            if (status === true && this.tempNoiseCancellationType !== this.currentNoiseCancellationType) {
                await this.syncSoundProcessorOptions();
            }

            const newStatus = await this.goloomSdk.session.setAudioStatus(status);

            if (typeof newStatus !== 'boolean') {
                logger.event('The invoked function returned an invalid value', {
                    additional: {
                        method: 'setAudioStatus',
                        type: 'audio',
                        status,
                        returnedStatus: newStatus,
                    },
                });
            }

            // Если при включении аудио ошибок не получили, то очищаем ошибки из стора
            if (newStatus) {
                this.onResetError.dispatch({ kind: 'audioinput' });
            }
        } catch (error) {
            this.onError.dispatch({
                kind: 'audioinput',
                error: getDeviceErrorCode(error),
            });

            logger.warn(error, {
                method: 'setAudioStatus',
                additional: {
                    activeDevice: this.audioInputActiveDevice,
                    selectedDevices: this.goloomSdk.session?.selectedDevices ?? {
                        audioInput: null,
                        videoInput: null,
                        audioOutput: null,
                    },
                },
            });

            throw error;
        }
    }

    public async setVideoStatus(status: boolean, isOptimistic = false) {
        if (!this.goloomSdk.session) {
            logger.event('Session is not initialized on setVideoStatus', {
                additional: {
                    method: 'setVideoStatus',
                    type: 'video',
                    status,
                },
            });
            return;
        }

        try {
            if (status === true && !this.goloomSdk.session.selectedDevices.videoInput && !isOptimistic) {
                throw new Error('No video input device selected');
            }
            if (status === true) {
                logger.startDelta(COUNTERS.UNMUTE_CAMERA_DELTA);
            }

            const newStatus = await this.goloomSdk.session.setVideoStatus(status);

            if (typeof newStatus !== 'boolean') {
                logger.event('The invoked function returned an invalid value', {
                    additional: {
                        method: 'setVideoStatus',
                        type: 'video',
                        status,
                        returnedStatus: newStatus,
                    },
                });
            }

            // Если при включении видео ошибок не получили, то очищаем ошибки из стора
            if (newStatus) {
                this.onResetError.dispatch({ kind: 'videoinput' });
            }
        } catch (error) {
            this.onError.dispatch({
                kind: 'videoinput',
                error: getDeviceErrorCode(error),
            });

            logger.warn(error, {
                method: 'setVideoStatus',
                additional: {
                    activeDevice: this.videoInputActiveDevice,
                    selectedDevices: this.goloomSdk.session?.selectedDevices ?? {
                        audioInput: null,
                        videoInput: null,
                        audioOutput: null,
                    },
                },
            });

            throw error;
        }
    }

    public get audioInputActiveDevice(): Maybe<AudioInputDevice | null> {
        return this.goloomSdk.session?.activeDevices.audioInput;
    }

    public get videoInputActiveDevice(): Maybe<VideoInputDevice | null> {
        return this.goloomSdk.session?.activeDevices.videoInput;
    }

    public get selectedDevices() {
        return this.goloomSdk.session?.selectedDevices;
    }

    public async getAudioInputPermission(): Promise<Maybe<PermissionState>> {
        // Firefox не поддерживает запрос статуса прав для микрофона и камеры
        if (
            !navigator?.permissions?.query ||
            browser()?.isFirefox() ||
            browser()?.isSafariBased() ||
            browser()?.isHeadlessChrome()
        ) {
            return;
        }

        logger.event('Trying to receive current microphone permission', {
            additional: {
                metod: 'getAudioInputPermission',
            },
        });

        return navigator.permissions
            .query({ name: 'microphone' as PermissionName })
            .then((result) => {
                logger.event('Received current microphone permission', {
                    additional: {
                        metod: 'getAudioInputPermission',
                        microphonePermission: result.state,
                    },
                });

                return result.state;
            })
            .catch((error) => {
                logger.warn(error, {
                    method: 'getAudioInputPermission',
                });

                throw error;
            });
    }

    public async getVideoInputPermission(): Promise<Maybe<PermissionState>> {
        // Firefox не поддерживает запрос статуса прав для микрофона и камеры
        if (!navigator?.permissions?.query || browser()?.isFirefox() || browser()?.isSafariBased()) {
            return;
        }

        logger.event('Trying to receive current camera permission', {
            additional: {
                metod: 'getVideoInputPermission',
            },
        });

        return navigator.permissions
            .query({ name: 'camera' as PermissionName })
            .then((result) => {
                logger.event('Received current camera permission', {
                    additional: {
                        metod: 'getVideoInputPermission',
                        cameraPermission: result.state,
                    },
                });

                return result.state;
            })
            .catch((error) => {
                logger.warn(error, {
                    method: 'getVideoInputPermission',
                });

                throw error;
            });
    }

    public syncMuteState(kind: 'audioinput' | 'videoinput' | 'all') {
        if (!this.goloomSdk.session) {
            logger.event('Session is not initialized on syncMuteState', {
                additional: {
                    method: 'syncMuteState',
                    kind,
                },
            });
            return;
        }

        if (kind === 'audioinput' || kind === 'all') {
            logger.event('Sync audio status', {
                additional: {
                    method: 'syncMuteState',
                    type: 'audio',
                    kind,
                    status: this.goloomSdk.session?.audioStatus,
                },
            });
            this.onAudioStatusChanged.dispatch(this.goloomSdk.session?.audioStatus);
        }

        if (kind === 'videoinput' || kind === 'all') {
            logger.event('Sync video status', {
                additional: {
                    method: 'syncMuteState',
                    type: 'video',
                    kind,
                    status: this.goloomSdk.session?.videoStatus,
                },
            });
            this.onVideoStatusChanged.dispatch(this.goloomSdk.session?.videoStatus);
        }
    }

    public get audioStatus() {
        if (!this.goloomSdk.session) {
            return;
        }
        return this.goloomSdk.session.audioStatus;
    }

    public get videoStatus() {
        if (!this.goloomSdk.session) {
            return;
        }
        return this.goloomSdk.session.videoStatus;
    }

    public get mutedState(): Record<'audio' | 'video', Maybe<boolean>> {
        return {
            video: this.videoStatus !== undefined ? !this.videoStatus : undefined,
            audio: this.audioStatus !== undefined ? !this.audioStatus : undefined,
        };
    }

    public async getLocalPreviewAudioStream(deviceId: Maybe<string>) {
        return this.sessionPreviewTaskQueue.runTask(async () => {
            if (!deviceId) {
                return;
            }

            let errorState = '';
            this.onResetError.dispatch({ kind: 'audioinput' });

            try {
                errorState = 'requestLocalPreviewDevice';
                const device = await this.getLocalAudioPreviewDevice(deviceId);
                if (device) {
                    await this.sessionPreview?.selectDevice(device);
                    await this.sessionPreview?.applySelectedDevices();
                    await this.sessionPreview?.setAudioStatus(true);

                    errorState = 'requestLocalPreviewDevice done';

                    if (this.sessionPreview?.activeDevices.audioInput) {
                        return this.createStreamFromDevice(this.sessionPreview?.activeDevices.audioInput);
                    }
                }
            } catch (error) {
                logger.error(error, {
                    method: 'getLocalPreviewAudioStream',
                    additional: {
                        state: errorState,
                    },
                });
                this.onError.dispatch({
                    kind: 'audioinput',
                    error: getDeviceErrorCode(error),
                });
            }
        }, PreviewDevicesTasks.GetPreviewAudioStream);
    }

    @bind
    public async getLocalPreviewCameraStream(deviceIdOrFacingMode: FacingMode | Maybe<string>) {
        return this.sessionPreviewTaskQueue.runTask(async () => {
            if (!deviceIdOrFacingMode) {
                return;
            }

            let errorState = '';
            this.onResetError.dispatch({ kind: 'videoinput' });

            try {
                const ua = browser();
                const isMobileLocalPreview =
                    ua &&
                    ua.isMobile() &&
                    (deviceIdOrFacingMode === FacingMode.USER || deviceIdOrFacingMode === FacingMode.ENVIRONMENT);

                errorState = 'requestLocalPreviewDevice';
                const device = isMobileLocalPreview
                    ? await this.getMobileLocalPreviewDevice(deviceIdOrFacingMode)
                    : await this.getDesktopLocalPreviewDevice(deviceIdOrFacingMode);

                const selectedDevice = this.sessionPreview?.selectedDevices.videoInput;
                // Проверяем что новый девайс отличается от выбранного по id. А если девайс не выбран, то применяем новый девайс
                const needApply =
                    !isNil(device) &&
                    (!selectedDevice ||
                        !('id' in selectedDevice) ||
                        !('id' in device) ||
                        selectedDevice.id !== device.id);
                // Если у нас не нашлось устройство в списке,
                // но его id равен default и до этого уже был выбран, то возвращаем его стрим
                if (
                    device ||
                    (deviceIdOrFacingMode === 'default' &&
                        selectedDevice &&
                        'id' in selectedDevice &&
                        selectedDevice?.id === deviceIdOrFacingMode &&
                        this.sessionPreview?.activeDevices.videoInput)
                ) {
                    if (needApply) {
                        await this.sessionPreview?.selectDevice(device);
                        await this.sessionPreview?.applySelectedDevices();
                    }
                    await this.sessionPreview?.setVideoStatus(true);

                    errorState = 'requestLocalPreviewDevice done';

                    if (this.sessionPreview?.activeDevices.videoInput) {
                        return this.createStreamFromDevice(this.sessionPreview?.activeDevices.videoInput);
                    }
                }
            } catch (error) {
                logger.error(error, {
                    method: 'getLocalPreviewCameraStream',
                    additional: {
                        state: errorState,
                    },
                });
                this.onError.dispatch({
                    kind: 'videoinput',
                    error: getDeviceErrorCode(error),
                });
            }
        }, PreviewDevicesTasks.GetPreviewVideoStream);
    }
    async stopPreviewVideoStream() {
        return this.sessionPreviewTaskQueue.runTask(async () => {
            return this.sessionPreview?.setVideoStatus(false);
        }, PreviewDevicesTasks.StopVideoStream);
    }
    public getActiveCameraStream() {
        const device = this.videoInputActiveDevice;
        if (device) {
            return this.createStreamFromDevice(device);
        }
    }

    public getActiveMicrophoneStream() {
        const device = this.audioInputActiveDevice;
        if (device) {
            return this.createStreamFromDevice(device);
        }
    }

    public setVideoProcessorOptions(videoProcessor: VideoProcessorOptions) {
        const tasks = [];
        this.initialConfig = {
            ...this.initialConfig,
            videoProcessing: videoProcessor,
        };
        if (this.sessionPreview) {
            tasks.push(this.sessionPreview.setVideoProcessorOptions(videoProcessor));
        } else {
            logger.error('No session preview found', {
                method: 'setVideoProcessorOptions',
            });
        }

        if (this.goloomSdk.session) {
            tasks.push(this.goloomSdk.setVideoProcessorOptions(videoProcessor));
        } else {
            logger.event('No goloomSdk session found', {
                additional: {
                    method: 'setVideoProcessorOptions',
                },
            });
        }

        if (!tasks.length) {
            return;
        }
        return Promise.all(tasks);
    }

    public async changeSoundProcessorOptions(noiseCancellationType: NoiseCancellationType): Promise<void> {
        if (this.goloomSdk.session?.audioStatus === true || !this.goloomSdk.session?.activeDevices.audioInput) {
            await this.setSoundProcessorOptions(noiseCancellationType);
            this.tempNoiseCancellationType = noiseCancellationType;
            this.currentNoiseCancellationType = noiseCancellationType;
            return;
        }

        /// выставляем шумодав для sessionPreview в любом случае
        await this.setSoundProcessorOptions(noiseCancellationType, {
            mainSession: false,
            sessionPreview: true,
        });
        this.tempNoiseCancellationType = noiseCancellationType;
    }
    private async syncSoundProcessorOptions() {
        //  этот метод временно нужен для фикса этого бага TELEMOSTFRONT-2876, в будущем нужно выпилить как только голум его пофиксит, выпиливаем здесь TELEMOSTFRONT-2899
        try {
            if (this.tempNoiseCancellationType === undefined) {
                throw new Error('No current noise cancellation type');
            }
            await this.setSoundProcessorOptions(this.tempNoiseCancellationType);
            this.currentNoiseCancellationType = this.tempNoiseCancellationType;
        } catch (error) {
            logger.warn(error, {
                method: 'setAudioStatus',
                additional: {
                    type: 'audio',
                    status,
                },
            });
        }
    }
    private setSoundProcessorOptions(
        noiseCancellationType: NoiseCancellationType,
        applyToSessions = {
            mainSession: true,
            sessionPreview: true,
        }
    ) {
        const noiseCancellation = getNoiseCancellationValueByType(noiseCancellationType);
        const currentSoundProcessorOptions = getSoundProcessingOptions();

        const soundProcessorOptions = {
            ...currentSoundProcessorOptions,
            noiseCancellation,
        };
        this.initialConfig = {
            ...this.initialConfig,
            soundProcessing: soundProcessorOptions,
        };

        const tasks = [];
        if (this.sessionPreview) {
            if (applyToSessions.sessionPreview) {
                tasks.push(this.sessionPreview.setSoundProcessorOptions(soundProcessorOptions));
            }
        } else {
            logger.event('No session preview found', {
                additional: {
                    method: 'setSoundProcessorOptions',
                },
            });
        }

        if (this.goloomSdk.session) {
            if (applyToSessions.mainSession) {
                tasks.push(this.goloomSdk.setSoundProcessorOptions(soundProcessorOptions));
            }
        } else {
            logger.event('No goloomSdk session found', {
                additional: {
                    method: 'setSoundProcessorOptions',
                },
            });
        }

        if (!tasks.length) {
            return;
        }
        return Promise.all(tasks);
    }
    @bind
    private async getMobileLocalPreviewDevice(facingMode: FacingMode): Promise<DeviceOrQuery> {
        if (this.videoInputActiveDevice) {
            return this.videoInputActiveDevice;
        }

        return {
            constraints: getMobileVideoConstraints(facingMode),
            kind: 'videoinput',
        };
    }

    @bind
    private async getDesktopLocalPreviewDevice(deviceId: string): Promise<Maybe<DeviceOrQuery>> {
        if (this.videoInputActiveDevice) {
            return this.videoInputActiveDevice;
        }

        const devices = await this.requestDevices({
            video: true,
            audio: false,
        });

        const device = devices.videoInput.find(({ id }) => id === deviceId);
        const videoConstraints = getDesktopVideoConstraints();
        if (device) {
            device.constraints = videoConstraints;
        }
        return device;
    }

    @bind
    private async getLocalAudioPreviewDevice(deviceId: string): Promise<Maybe<DeviceOrQuery>> {
        if (this.audioInputActiveDevice) {
            return this.audioInputActiveDevice;
        }

        const devices = await this.requestDevices({
            video: false,
            audio: true,
        });

        const device = devices.audioInput.find(({ id }) => id === deviceId);
        return device;
    }

    private createStreamFromDevice(device: Device) {
        if (isInputDevice(device) && device.track) {
            return new MediaStream([device.track]);
        }
    }

    @bind
    private async handleAudioStatusChanged(event: SessionEvent['audioStatusChanged']) {
        if (!this.goloomSdk.session) {
            logger.event('Session is not initialized on audioStatusChanged', {
                additional: {
                    method: 'handleAudioStatusChanged',
                    status: event.status,
                },
            });
            return;
        }

        // Голум триггерит событие немедленно при вызове setVideoStatus с желаемым статусом до получения доступа к
        // девайсам - https://st.yandex-team.ru/CHEMODAN-91612#6650e26eeb0bc547b9e664a7
        // Если приходит событие audioStatusChanged с status: true, хотя браузер в процессе запроса прав на эти
        // устройства, то они должны быть замьючены.
        // Если status: false, проверку не делаем, так как activeDevices могут быть пустыми.
        if (event.status && !this.goloomSdk.session.activeDevices.audioInput) {
            logger.event('No active devices on audioStatusChanged', {
                additional: {
                    method: 'handleAudioStatusChanged',
                },
            });
            return;
        }

        if (event.status === true) {
            logger.endDelta(COUNTERS.UNMUTE_MICROPHONE_DELTA);
        }

        logger.event('Handle event audioStatusChanged', {
            additional: {
                method: 'handleAudioStatusChanged',
                type: 'audio',
                status: event.status,
            },
        });

        this.onAudioStatusChanged.dispatch(event.status);
    }

    @bind
    private async handleVideoStatusChanged(event: SessionEvent['videoStatusChanged']) {
        if (!this.goloomSdk.session) {
            logger.event('Session is not initialized on videoStatusChanged', {
                additional: {
                    method: 'handleVideoStatusChanged',
                    status: event.status,
                },
            });
            return;
        }

        // Голум триггерит событие немедленно при вызове setVideoStatus с желаемым статусом до получения доступа к
        // девайсам - https://st.yandex-team.ru/CHEMODAN-91612#6650e26eeb0bc547b9e664a7
        // Если приходит событие videoStatusChanged с status: true, хотя браузер в процессе запроса прав на эти
        // устройства, то они должны быть замьючены.
        // Если status: false, проверку не делаем, так как activeDevices могут быть пустыми.
        if (event.status && !this.goloomSdk.session.activeDevices.videoInput) {
            logger.event('No active devices on videoStatusChanged', {
                additional: {
                    method: 'handleVideoStatusChanged',
                },
            });
            return;
        }

        if (event.status === true) {
            logger.endDelta(COUNTERS.UNMUTE_CAMERA_DELTA);
        }

        logger.event('Handle event videoStatusChanged', {
            additional: {
                method: 'handleVideoStatusChanged',
                type: 'video',
                status: event.status,
            },
        });

        this.onVideoStatusChanged.dispatch(event.status);
    }

    @bind
    private handleActiveDevicesChanged(event: SessionEvent['activeDevicesChanged']) {
        if (!this.goloomSdk.session) {
            return;
        }
        logger.event('Active devices changed', {
            additional: {
                method: 'handleActiveDevicesChanged',
                activeDevices: event.activeDevices,
            },
        });

        this.onActiveDevicesChanged.dispatch(event.activeDevices);
    }

    @bind
    private handleDeviceChanged(event: SessionEvent['deviceChanged']) {
        if (!this.goloomSdk.session) {
            return;
        }
        logger.event('Devices changed', {
            additional: {
                method: 'handleDeviceChanged',
                ...event,
            },
        });
        this.onDeviceChanged.dispatch(event);
    }

    @bind
    private handlePreviewActiveDevicesChanged(event: SessionEvent['activeDevicesChanged']) {
        if (this.goloomSdk.session) {
            return;
        }
        logger.event('Active devices changed', {
            additional: {
                method: 'handlePreviewActiveDevicesChanged',
                activeDevices: event.activeDevices,
            },
        });

        this.onActiveDevicesChanged.dispatch(event.activeDevices);
    }

    @bind
    private handlePreviewDeviceChanged(event: SessionEvent['deviceChanged']) {
        if (this.goloomSdk.session) {
            return;
        }
        logger.event('Devices changed', {
            additional: {
                method: 'handlePreviewDeviceChanged',
                ...event,
            },
        });
        this.onDeviceChanged.dispatch(event);
    }

    @bind
    private syncMuteStateOnJoin() {
        this.syncMuteState('all');
    }

    @bind
    private subscribeToEvents() {
        if (!this.goloomSdk.session) {
            logger.event('Session is not initialized on subscribeToEvents', {
                additional: {
                    method: 'subscribeToEvents',
                },
            });
            return;
        }

        this.goloomSdk.session.addAsyncEventListener('audioStatusChanged', this.handleAudioStatusChanged);
        this.goloomSdk.session.addAsyncEventListener('videoStatusChanged', this.handleVideoStatusChanged);
        this.goloomSdk.session.addAsyncEventListener('activeDevicesChanged', this.handleActiveDevicesChanged);
        this.goloomSdk.session.addAsyncEventListener('deviceChanged', this.handleDeviceChanged);
    }

    @bind
    private unsubscribeFromEvents() {
        if (!this.goloomSdk.session) {
            logger.event('Session is not initialized on unsubscribeFromEvents', {
                additional: {
                    method: 'unsubscribeFromEvents',
                },
            });
            return;
        }

        this.goloomSdk.session.removeAsyncEventListener('audioStatusChanged', this.handleAudioStatusChanged);
        this.goloomSdk.session.removeAsyncEventListener('videoStatusChanged', this.handleVideoStatusChanged);
        this.goloomSdk.session.removeAsyncEventListener('activeDevicesChanged', this.handleActiveDevicesChanged);
        this.goloomSdk.session.removeAsyncEventListener('deviceChanged', this.handleDeviceChanged);
    }
}
